// Artist profile create + update.
//
// Wave B refactor: `art_forms` validates structurally via `slugSchema`. The
// existence check (slug present + enabled in art_forms table) happens in the
// worker route handler against the live KV vocab snapshot. See venues.ts for
// the rationale.
import { z } from 'zod';

import {
  countryCodeSchema,
  httpsUrlSchema,
  imageRefSchema,
  latSchema,
  lngSchema,
  richTextSchema,
  slugSchema,
  vocabSlugSchema,
} from './common.js';

const linkKindSchema = z.enum([
  'website',
  'instagram',
  'spotify',
  'bandcamp',
  'soundcloud',
  'mixcloud',
  'youtube',
  'vimeo',
  'tiktok',
  'facebook',
  'apple_music',
  'x',
  'other',
]);

export const artistLinkSchema = z.object({
  kind: linkKindSchema,
  url: httpsUrlSchema,
  label: z.string().min(1).max(80).optional(),
});

export const updateArtistProfileInputSchema = z
  .object({
    slug: slugSchema.optional(),
    bio: richTextSchema(2000).optional(),
    city: z.string().max(80).optional(),
    country: countryCodeSchema.optional(),
    home_lat: latSchema.optional(),
    home_lng: lngSchema.optional(),
    discipline_summary: z.string().max(160).optional(),
    art_forms: z.array(vocabSlugSchema).min(1).max(8).optional(),
    // Free-form genre tags per North Star §4. Lowercased + deduped on the
    // client; we re-normalise here to belt-and-braces against API callers.
    genres: z
      .array(z.string().trim().min(1).max(40).transform((s) => s.toLowerCase()))
      .max(8)
      .optional()
      .transform((arr) => (arr === undefined ? undefined : Array.from(new Set(arr)))),
    links: z.array(artistLinkSchema).max(10).optional(),
    // Both can be populated by our own uploader, which mints same-origin paths —
    // so they take imageRefSchema, not httpsUrlSchema. Artist *links* below stay
    // https-only; those are always off-platform destinations.
    profile_photo_url: imageRefSchema.optional(),
    gallery_image_urls: z.array(imageRefSchema).max(12).optional(),
    party_size_default: z.number().int().positive().max(50).optional(),
    dietary_notes: z.string().max(280).optional(),
    sleeping_preferences: z.string().max(280).optional(),
    accessibility_needs: z.string().max(280).optional(),
    published: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Pass at least one field to update.' });
export type UpdateArtistProfileInput = z.infer<typeof updateArtistProfileInputSchema>;

export const publishArtistProfileInputSchema = z.object({
  published: z.boolean(),
});
export type PublishArtistProfileInput = z.infer<typeof publishArtistProfileInputSchema>;
